import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { blogArticles } from "@/mocks/blog-articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuypolska.com";

function generateArticleContent(keyword: string, title: string, category: string): string[] {
  const kw = keyword || title;
  const isComparison = category === "Porównanie";
  const isSafety = category === "Bezpieczeństwo";
  const isShipping = category === "Wysyłka";
  const isRanking = category === "Ranking";
  const isGuide = category === "Przewodnik" || category === "Tutorial";
  const isStyle = category === "Styl";

  const paragraphs: string[] = [];

  paragraphs.push(
    `Jeśli szukasz rzetelnych informacji na temat <strong>${kw}</strong>, trafiłeś we właściwe miejsce. W tym artykule znajdziesz wszystko, co musisz wiedzieć — od podstawowych informacji po zaawansowane porady, które pomogą Ci podejmować lepsze decyzje zakupowe na platformie Kakobuy.`
  );

  if (isGuide) {
    paragraphs.push(
      `Kakobuy to jedna z najpopularniejszych platform agentskich umożliwiających zakupy z Chin. Dzięki ${kw} możesz zaoszczędzić czas, pieniądze i uniknąć wielu błędów, które popełniają początkujący użytkownicy. Platforma oferuje dostęp do milionów produktów z Taobao, Weidian i 1688 — wszystko w jednym miejscu, z polskim interfejsem i obsługą.`
    );
  } else if (isComparison) {
    paragraphs.push(
      `Wybór odpowiedniej platformy zakupowej z Chin może być trudny. W tym porównaniu ${kw} przeanalizujemy wszystkie kluczowe aspekty — od cen i szybkości wysyłki, przez jakość obsługi klienta, aż po bezpieczeństwo transakcji. Nasz zespół przetestował obie platformy, aby dostarczyć Ci najbardziej obiektywną analizę.`
    );
  } else if (isSafety) {
    paragraphs.push(
      `Bezpieczeństwo zakupów online to priorytet każdego świadomego konsumenta. W kontekście ${kw} istnieje wiele mitów i nieporozumień. W tym artykule rozwiejemy wszelkie wątpliwości i przedstawimy konkretne strategie, które pozwolą Ci robić zakupy bez obaw.`
    );
  } else if (isShipping) {
    paragraphs.push(
      `Logistyka i wysyłka to często najbardziej stresujący element zakupów z Chin. W tym artykule szczegółowo wyjaśniamy ${kw} — od wyboru linii wysyłkowej, przez śledzenie paczki, aż po odbiór przesyłki w Polsce.`
    );
  } else if (isRanking) {
    paragraphs.push(
      `Szukasz najlepszych produktów i nie chcesz tracić czasu na przeglądanie setek ofert? Nasz ${kw} to rezultat wielogodzinnych badań, analiz i opinii społeczności. Znajdziesz tu tylko sprawdzone produkty z potwierdzoną jakością.`
    );
  } else if (isStyle) {
    paragraphs.push(
      `Moda uliczna i streetwear to nie tylko ubrania — to styl życia. Dzięki ${kw} możesz odkryć świat chińskiego streetwearu i zbudować garderobę, która wyraża Twój charakter, nie rujnując przy tym portfela.`
    );
  } else {
    paragraphs.push(
      `Kakobuy Spreadsheet to największa polska baza produktów z Chin. ${kw} to jeden z najczęściej wyszukiwanych tematów przez użytkowników z Polski, dlatego przygotowaliśmy ten kompletny przewodnik, który odpowie na wszystkie Twoje pytania.`
    );
  }

  paragraphs.push(
    `Zakupy przez chińskie platformy agentskie stają się coraz popularniejsze w Polsce. Głównym powodem jest ogromny wybór produktów w cenach znacznie niższych niż w europejskich sklepach. Dzięki <strong>Kakobuy Spreadsheet</strong> masz wszystkie najlepsze produkty w jednym miejscu — z gotowymi linkami, zdjęciami QC i opiniami innych użytkowników.`
  );

  paragraphs.push(
    `Kluczem do udanych zakupów na Kakobuy jest wiedza. Im więcej wiesz o platformie, jej mechanizmach i potencjalnych pułapkach, tym lepsze decyzje podejmujesz. Nasz zespół regularnie testuje nowe produkty, weryfikuje sprzedawców i aktualizuje bazę, abyś zawsze miał dostęp do najbardziej aktualnych informacji.`
  );

  paragraphs.push(
    `W dalszej części tego artykułu znajdziesz szczegółowe informacje na temat ${kw}, praktyczne porady, przykłady z życia wzięte i odpowiedzi na najczęściej zadawane pytania. Niezależnie od tego, czy jesteś początkującym, czy doświadczonym użytkownikiem Kakobuy — ten przewodnik ma dla Ciebie wartość.`
  );

  return paragraphs;
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = contentRef.current?.querySelectorAll(".reveal-content");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-[100dvh] flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Nie znaleziono artykułu</h1>
            <p className="text-sm text-foreground-600 mb-6">Przepraszamy, ten artykuł nie istnieje lub został przeniesiony.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-medium px-6 py-3 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              Wróć do bloga
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const contentParagraphs = generateArticleContent(article.keyword, article.title, article.category);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: "2026-07-20",
    author: {
      "@type": "Organization",
      name: "Kakobuy Spreadsheet Polska",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Kakobuy Spreadsheet Polska",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/${article.slug}`,
    },
  };

  return (
    <div className="min-h-[100dvh] flex flex-col">
      <JsonLd data={articleJsonLd} />
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Article Header */}
        <section className="relative">
          <div className="w-full h-[280px] md:h-[400px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-foreground-950/20" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 lg:px-12 pb-6 md:pb-10">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-block text-xs font-medium text-primary-300 bg-primary-500/20 px-3 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-background-300/80">{article.date}</span>
                <span className="text-xs text-background-300/80">{article.readTime} czytania</span>
              </div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section ref={contentRef} className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Table of Contents */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 mb-8">
              <h2 className="text-sm font-semibold text-foreground-950 mb-3">Spis treści</h2>
              <ol className="flex flex-col gap-1.5">
                {["Wprowadzenie do tematu", "Dlaczego to jest ważne?", "Szczegółowa analiza", "Praktyczne porady", "Częste pytania i odpowiedzi"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 text-xs font-semibold flex-shrink-0">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ol>
            </div>

            {/* Intro */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-100">
              <div
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 article-content"
                dangerouslySetInnerHTML={{ __html: contentParagraphs[0] }}
              />
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                {contentParagraphs[1]}
              </p>
            </div>

            {/* Why it matters */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-150">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 mt-8">
                Dlaczego {article.keyword} jest ważne dla kupujących z Polski?
              </h2>
              <div
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 article-content"
                dangerouslySetInnerHTML={{ __html: contentParagraphs[2] }}
              />
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {contentParagraphs[3]}
              </p>
            </div>

            {/* Key Benefits */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-200">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 mt-8">
                Kluczowe korzyści dla użytkowników
              </h2>
              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 mb-6">
                <ul className="flex flex-col gap-3">
                  {[
                    "Oszczędność czasu — nie musisz przeszukiwać setek ofert ręcznie",
                    "Sprawdzone linki — każdy produkt ma gotowy link do zakupu",
                    "Zdjęcia QC — sprawdź jakość produktu przed wysyłką",
                    "Aktualne informacje — baza jest regularnie aktualizowana",
                    "Opinie społeczności — ucz się na doświadczeniach innych",
                    "Bezpieczeństwo — sprawdzone strategie zakupowe",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Detailed Analysis */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-250">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 mt-8">
                Szczegółowa analiza {article.keyword}
              </h2>
              <div
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 article-content"
                dangerouslySetInnerHTML={{ __html: contentParagraphs[4] }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {[
                  { lbl: "Jakość informacji", val: "Bardzo wysoka", icon: "ri-star-line" },
                  { lbl: "Dostępność", val: "24/7 online", icon: "ri-time-line" },
                  { lbl: "Aktualizacje", val: "Codziennie", icon: "ri-refresh-line" },
                  { lbl: "Wsparcie", val: "Społeczność + poradniki", icon: "ri-group-line" },
                ].map((s) => (
                  <div key={s.lbl} className="bg-background-100 rounded-xl p-4 border border-background-200/60">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className={`${s.icon} text-lg`} />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-950">{s.lbl}</h4>
                        <p className="text-xs text-foreground-600">{s.val}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Practical Tips */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-300">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 mt-8">
                Praktyczne porady — jak najlepiej wykorzystać {article.keyword}
              </h2>
              <div className="flex flex-col gap-3 mb-6">
                {[
                  "Zawsze sprawdzaj aktualność linków przed zakupem — produkty mogą się wyprzedać",
                  "Porównuj ceny między różnymi sprzedawcami tego samego produktu",
                  "Czytaj opinie innych użytkowników przed podjęciem decyzji",
                  "Korzystaj ze zdjęć QC przed zatwierdzeniem wysyłki międzynarodowej",
                  "Zapisuj interesujące Cię produkty do ulubionych, aby łatwo do nich wrócić",
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/60">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-sm text-foreground-700 leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-350">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 mt-8">
                Najczęściej zadawane pytania
              </h2>
              <div className="flex flex-col gap-3 mb-6">
                {[
                  { q: `Czy ${article.keyword} jest odpowiednie dla początkujących?`, a: "Tak, nasze przewodniki są napisane prostym językiem i zawierają instrukcje krok po kroku, które pomogą Ci zacząć od podstaw." },
                  { q: "Jak często aktualizowane są informacje?", a: "Nasza baza jest aktualizowana codziennie. Linki są weryfikowane, a nieaktualne produkty usuwane." },
                  { q: "Czy potrzebuję specjalnego konta?", a: "Do przeglądania Kakobuy Spreadsheet nie potrzebujesz konta. Do zakupów na Kakobuy wymagana jest bezpłatna rejestracja." },
                ].map((faq, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">{faq.q}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-400">
              <div className="bg-primary-500/5 rounded-xl p-6 md:p-8 border border-primary-200/30 text-center mt-8">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  Gotowy aby zacząć?
                </h3>
                <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
                  Przejdź do pełnej bazy Kakobuy Spreadsheet i wykorzystaj swoją nową wiedzę w praktyce. Tysiące produktów czeka na Ciebie.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Kakobuy Spreadsheet
                    <i className="ri-external-link-line" />
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-background-100 text-foreground-950 text-sm font-medium px-6 py-2.5 rounded-full border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    Buy it now
                    <i className="ri-shopping-bag-line" />
                  </a>
                </div>
              </div>
            </div>

            {/* Internal Links */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-450">
              <div className="mt-10 pt-6 border-t border-background-200">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3">
                  Przeczytaj również:
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Link to="/" className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    Strona główna
                  </Link>
                  <Link to="/blog" className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    Wszystkie artykuły
                  </Link>
                  <Link to="/kakobuy-spreadsheet" className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    Kakobuy Spreadsheet
                  </Link>
                  <Link to="/guides" className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    Poradnik zakupowy
                  </Link>
                  <Link to="/faq" className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors">
                    FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        .reveal-content.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}